import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, filter, switchMap, distinctUntilChanged, catchError, shareReplay } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form';
import { MmProjectLoaderService } from '../../mm-project/mm-project-loader.service';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsAttributeFilterService {
  private readonly EMPTY_VALUE = EMPTY_FILTER_ID;
  private attributesSubject = new BehaviorSubject<SelectItem[]>([]);
  private attributeGroupsSubject = new BehaviorSubject<SelectItem[]>([]);
  
  public attributes$ = this.attributesSubject.asObservable();
  public attributesWithoutEmpty$ = this.attributesSubject.pipe(
    map(attributes => attributes.filter(attr => attr.id !== this.EMPTY_VALUE))
  );
  public attributeGroups$ = this.attributeGroupsSubject.asObservable();

  constructor(
    private projectLoaderService: MmProjectLoaderService,
    private translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initializeAttributeListeners();
  }

  private initializeAttributeListeners(): void {
    this.projectLoaderService.attributeGroups.pipe(
      filter(ag => ag?.loadState?.status === LoadStatusEnum.Loaded),
      switchMap(attributeGroups => 
        combineLatest([
          of(attributeGroups),
          this.projectLoaderService.attributes.pipe(
            filter(a => a?.loadState?.status === LoadStatusEnum.Loaded)
          ),
          this.projectLoaderService.attributeGroupsFiles.pipe(
            filter(agf => agf?.loadState?.status === LoadStatusEnum.Loaded)
          )
        ])
      ),
      map(([attributeGroups, attributes, attributeGroupsFiles]) => {
        const usedGroupIds = attributes.data
          .filter(attr => !attr.IsDeleted && !attr.IsInactive)
          .map(attr => attr.AttributeGroup?.Id)
          .filter((id, index, self) => id && self.indexOf(id) === index);

        return attributeGroups.data
          .filter(group => usedGroupIds.includes(group.Id))
          .map(group => ({
            id: group.Id?.toString() || '',
            text: group.Name1 || '',
            data: attributeGroupsFiles.data.find(gf => gf.Id === group.Id) // Attach image data
          }))
          .sort((a, b) => {
            const idA = parseInt(a.id) || 0;
            const idB = parseInt(b.id) || 0;
            return idA - idB;
          });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      catchError(error => {
        this.logger.error(error, '', 'FieldsAttributeFilterService');
        return of([]);
      })
    ).subscribe(groups => {
      const emptyOption = this.getEmptyOption();
      this.attributeGroupsSubject.next([emptyOption, ...groups]);
    });

    this.projectLoaderService.attributes.pipe(
      filter(a => a?.loadState?.status === LoadStatusEnum.Loaded),
      map(attributes => {
        return attributes.data
          .filter(attr => !attr.IsDeleted && !attr.IsInactive)
          .map(attr => {
            const name1 = attr.Name1 || '';
            const name2 = attr.Name2 || '';
            const text = (name1 && name2) ? `${name1} - ${name2}` : (name1 || name2 || '');
            return ({
              id: attr.Id?.toString() || '',
              text,
              data: { groupId: attr.AttributeGroup?.Id }
            });
          })
          .sort((a, b) => {
            const idA = parseInt(a.id) || 0;
            const idB = parseInt(b.id) || 0;
            return idA - idB;
          });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      catchError(error => {
        this.logger.error(error, '', 'FieldsAttributeFilterService');
        return of([]);
      })
    ).subscribe(attributes => {
      const emptyOption = this.getEmptyOption();
      this.attributesSubject.next([emptyOption, ...attributes]);
    });
  }

  private getEmptyOption(): SelectItem {
    const emptyText = this.translateService.instant('MM.fields.filter.empty');
    return {
      id: this.EMPTY_VALUE,
      text: emptyText
    };
  }

  public getAttributesForGroup(groupId: string): Observable<SelectItem[]> {
    if (!groupId || groupId === this.EMPTY_VALUE) {
      return this.attributes$;
    }

    return this.attributes$.pipe(
      map(attributes => {
        const emptyOption = attributes.find(attr => attr.id === this.EMPTY_VALUE);
        const filteredAttributes = attributes.filter(attr => 
          attr.id === this.EMPTY_VALUE || attr.data?.groupId?.toString() === groupId
        );
        return emptyOption ? [emptyOption, ...filteredAttributes.filter(a => a.id !== this.EMPTY_VALUE)] : filteredAttributes;
      })
    );
  }

  public getDisplayTextForAttribute(attributeId: string): Observable<string> {
    if (attributeId === this.EMPTY_VALUE) {
      return of(this.translateService.instant('MM.fields.filter.empty'));
    }

    return this.attributes$.pipe(
      map(attributes => {
        const attr = attributes.find(a => a.id === attributeId);
        return attr?.text || attributeId;
      })
    );
  }

  public getDisplayTextForAttributeGroup(groupId: string): Observable<string> {
    if (groupId === this.EMPTY_VALUE) {
      return of(this.translateService.instant('MM.fields.filter.empty'));
    }

    return this.attributeGroups$.pipe(
      map(groups => {
        const group = groups.find(g => g.id === groupId);
        return group?.text || groupId;
      })
    );
  }
}